import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";

/**
 * Form : famille "formulaire" (questionnaire).
 *
 * Modèle de questions versionné défini par l'organisation, et les réponses
 * structurées collectées, conservées avec la version du modèle utilisé.
 * Construite sur le modèle "famille + type" de la bible (patron : `asset`).
 *
 * Le socle ne connaît pas le questionnaire concret ("fiche d'admission",
 * "enquête de satisfaction") : il connaît la famille générique `form`. Le
 * vocabulaire métier est porté par le champ `type` (chaîne libre déclarée par
 * l'app). Le modèle des questions vit dans `templateSchema`, sa version dans
 * `templateVersion`.
 *
 * Sujet du formulaire (le `subjectRef` de la fiche) : polymorphe, on l'expose
 * en ids concrets optionnels (même traitement que care-record / work-order /
 * listing), un seul étant en général renseigné : `bookingId` (questionnaire
 * préalable au rendez-vous), `eventId` (inscription documentée), `custodyId`
 * (fiche d'admission de la garde), `reviewId` (enquête qui produit un avis),
 * `consentId` (recueil adossé au formulaire). Le répondant identifié est un
 * `contact` via `submittedByContactId`.
 *
 * Références vers d'autres familles = de simples ids (aucun import de schéma).
 *
 * Sécurité / RGPD : le champ `sensitivity` (normal, personnel, sante) est
 * OBLIGATOIRE et classe le questionnaire ; les réponses en héritent. Le contenu
 * collecté (`answers`) peut porter de la donnée de santé (RGPD art. 9) : un
 * questionnaire sante est owner + rôle désigné, jamais org-wide, stocké à part.
 * Il n'entre donc PAS dans le schéma v0 (cf. `answers` ci-dessous). Tout
 * formulaire qui concerne un mineur applique le niveau d'accès le plus
 * restrictif. En v0 la famille reste org-wide (même doctrine qu'asset) ;
 * l'accès fin (owner + rôle, cloisonnement des sujets sensibles) viendra après.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Champs métier de la famille form (hors champs standard du socle). */
export const formCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "admission", "satisfaction"). */
  type: z.string().min(1),
  /** Classification obligatoire dont les réponses héritent (ex. "normal", "personnel", "sante"). */
  sensitivity: z.string().min(1),
  /** Modèle des questions défini par l'org. Liste structurée (une entrée par question). Optionnel. */
  templateSchema: z.array(z.record(z.string(), z.unknown())).optional(),
  /** Version du modèle de questions utilisé, conservée avec les réponses. Optionnel. */
  templateVersion: z.number().int().optional(),
  /** Sujet : rendez-vous concerné (référence `booking`), branche du sujet polymorphe. Optionnel. */
  bookingId: z.string().min(1).optional(),
  /** Sujet : occurrence concernée (référence `event`), branche du sujet polymorphe. Optionnel. */
  eventId: z.string().min(1).optional(),
  /** Sujet : prise en charge concernée (référence `custody`), branche du sujet polymorphe. Optionnel. */
  custodyId: z.string().min(1).optional(),
  /** Sujet : avis produit (référence `review`), branche du sujet polymorphe. Optionnel. */
  reviewId: z.string().min(1).optional(),
  /** Sujet : consentement adossé (référence `consent`), branche du sujet polymorphe. Optionnel. */
  consentId: z.string().min(1).optional(),
  /** Répondant identifié (référence `contact`). Optionnel. */
  submittedByContactId: z.string().min(1).optional(),
  /** Date de soumission des réponses. Optionnel. */
  submittedAt: z.date().optional(),
  // answers : sensible (réponses collectées, potentiellement de santé, RGPD art. 9), ira dans un sous-document privé owner + rôle désigné (cf. WIP), hors v0.
} as const;

/** Schéma complet de la famille form (champs standard + champs métier). */
export const FormSchema = z.object({
  ...baseEntityFields,
  ...formCoreFields,
});

/** Type d'un form. */
export type Form = z.infer<typeof FormSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const FormCreateInputSchema = z.object({
  ...formCoreFields,
  ...customFieldsField,
});

/** Type de l'entrée de création d'un form. */
export type FormCreateInput = z.infer<typeof FormCreateInputSchema>;

/**
 * Schéma du patch de mise à jour (update partiel).
 * `orgId` et `type` sont immuables (Security Rules) ; les champs standard
 * sont gérés par le SDK : aucun des deux n'est accepté ici.
 */
export const FormUpdateInputSchema = z
  .object({ ...formCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true })
  .partial();

/** Type du patch de mise à jour d'un form. */
export type FormUpdateInput = z.infer<typeof FormUpdateInputSchema>;
